/**
 * src/report-engine/templates.tsx
 *
 * Three report templates: monthly, quarterly, annual.
 *
 * Cover layout (all templates):
 *   • Dark header bar (black, logo left, title right)
 *   • Photo (top portion)
 *   • Below photo: 3 two-column data tables
 *       1. Fiche identité du site
 *       2. Usage énergétique
 *       3. Comptage
 *   Each table has a full-width black title row and label/value rows.
 */

import type { ReportTemplate, CoverProps } from "./types";

const FF_TITLE = "'Avenir Black','Nunito Variable',sans-serif";
const FF_BODY  = "'Avenir Book','Nunito Variable',sans-serif";

// ── Shared two-column data table ──────────────────────────────────────────────

interface InfoTableProps {
  title:  string;
  rows:   { label: string; value: string }[];
  accent?: string;
  headerId?: string; // stable hook for overlay controls to locate this title row in the DOM
}
function InfoTable({ title, rows, accent = "#00FF00", headerId }: InfoTableProps) {
  return (
    <div style={{ marginBottom: 10, breakInside: "avoid" }}>
      {/* Title row */}
      <div data-cover-header={headerId} style={{ background: "#000", padding: "5px 12px", display: "flex", alignItems: "center" }}>
        <span style={{ fontFamily: FF_TITLE, fontSize: 9, fontWeight: 900, color: accent, letterSpacing: 1.5, textTransform: "uppercase" }}>
          {title}
        </span>
      </div>
      {/* Data rows */}
      {rows.map((row, i) => (
        <div key={i} style={{
          display: "flex", alignItems: "stretch",
          background: i % 2 === 0 ? "#fff" : "#f5f5f5",
          borderBottom: "1px solid #e8e8e8",
        }}>
          <div style={{ width: "45%", padding: "5px 12px", fontFamily: FF_BODY, fontSize: 9, color: "#666", borderRight: "1px solid #e8e8e8", flexShrink: 0 }}>
            {row.label}
          </div>
          <div style={{ flex: 1, padding: "5px 12px", fontFamily: FF_BODY, fontSize: 9, color: "#1a1a2e", fontWeight: 400 }}>
            {row.value || "—"}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Cover component factory — shared layout, different accent/title ───────────

function makeCover(templateLabel: string, accent: string) {
  return function CoverComponent(props: CoverProps) {
    const p = props as Record<string, string>;
    const {
      companyName = "Votre Entreprise",
      siteName    = "",
      periodLabel = "",
      photoUrl    = "",
      // Fiche identité
      adresse = "", occupant = "", anneeConstruction = "", anneeRehabiliation = "",
      configuration = "", typologie = "", surface = "", horaire = "",
      // Usage énergétique
      chauffage = "", rafraichissement = "",
      // Comptage — electricite and gaz may be JSON arrays
      electricite = "", gaz = "",
      clientName = "",
      // Source de données
      gtb = "",
    } = p;

    // Count PDL/PCE entries — JSON array or non-empty plain string counts as 1
    const countMeter = (raw: string): number => {
      try { const a = JSON.parse(raw); if (Array.isArray(a)) return a.filter(Boolean).length; } catch {}
      return raw.trim() ? 1 : 0;
    };
    const elecCount = countMeter(electricite);
    const gazCount  = countMeter(gaz);
    const electriciteDisplay = elecCount > 0 ? `${elecCount} PRM` : "";
    const gazDisplay         = gazCount  > 0 ? `${gazCount} PCE`  : "";

    return (
      <div style={{ width: "100%", flex: 1, background: "#fafafa", display: "flex", flexDirection: "column", fontFamily: FF_BODY }}>

        {/* ── Dark header bar ── */}
        <div data-a4-header="" style={{ background: "#000", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <img src="/logo.png" alt="logo" style={{ height: 26, objectFit: "contain", flexShrink: 0 }}/>
          <div style={{ textAlign: "right" }}>
            {/* Line 1: template label — period */}
            <div style={{ fontFamily: FF_TITLE, fontSize: 8, letterSpacing: 3, color: accent, textTransform: "uppercase" }}>
              {templateLabel}{periodLabel ? ` — ${periodLabel}` : ""}
            </div>
            {/* Line 2: client name (falls back to companyName for legacy reports) */}
            <div style={{ fontFamily: FF_TITLE, fontSize: 15, fontWeight: 900, color: "#fff", marginTop: 2 }}>{clientName || companyName}</div>
            {/* Line 3: site name — same colour as companyName, fontSize 12 */}
            {siteName && <div style={{ fontFamily: FF_TITLE, fontSize: 12, fontWeight: 900, color: "#fff", marginTop: 1 }}>{siteName}</div>}
          </div>
        </div>

        {/* ── Photo — full width, aspect ratio preserved, max 40% of page height ── */}
        {photoUrl ? (
          <div style={{ flexShrink: 0, background: "#222", lineHeight: 0 }}>
            <img
              src={photoUrl}
              alt="site"
             
              style={{ width: "100%", objectFit: "cover", display: "block" }}
            />
          </div>
        ) : (
          <div style={{ height: "33%", flexShrink: 0, background: "#222", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 6, color: "#555" }}>
            <span style={{ fontSize: 32 }}>📷</span>
            <span style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>Photo du site</span>
          </div>
        )}

        {/* ── Three data tables — fill remaining space, footer stays pinned ── */}
        <div style={{ flex: 1, padding: "10px 12px 0", overflow: "hidden" }}>
          <InfoTable title="Fiche identité du site" accent={accent} headerId="fiche-identite" rows={[
            { label: "Adresse",               value: adresse },
            { label: "Occupant",              value: occupant },
            { label: "Année de construction", value: anneeConstruction },
            { label: "Année de réhabilitation", value: anneeRehabiliation },
            { label: "Configuration",         value: configuration },
            { label: "Typologie",             value: typologie },
            { label: "Surface",               value: surface },
            { label: "Horaire",               value: horaire },
          ]}/>
          <InfoTable title="Usage énergétique" accent={accent} rows={[
            { label: "Chauffage",      value: chauffage },
            { label: "Rafraîchissement", value: rafraichissement },
          ]}/>
          <InfoTable title="Comptage" accent={accent} rows={[
            { label: "Électricité", value: electriciteDisplay },
            { label: "Gaz",         value: gazDisplay },
          ]}/>
          {gtb.trim() && (
            <InfoTable title="Source de données" accent={accent} rows={[
              { label: "GTB / Supervision", value: gtb },
            ]}/>
          )}
        </div>

      </div>
    );
  };
}

// Instantiate the three cover components
const MonthlyCover  = makeCover("Rapport Mensuel", "#00FF00");
const QuarterlyCover = makeCover("Rapport Trimestriel", "#00FF00");
const AnnualCover    = makeCover("Rapport Annuel", "#00FF00");

// ── Cover for "mensuel-multi" — no single site, just the client name + a
// centered list of site names (no photo, no InfoTable — there's no one site
// to describe). Deliberately not built from makeCover().
interface MultiCoverSite { name: string; photoUrl?: string; }

const MULTI_COVER_GAP = 14;
const MULTI_COVER_ROW_GAP = 28;
const MULTI_COVER_MAX_PER_ROW = 3;

// Split n sites into rows of at most MULTI_COVER_MAX_PER_ROW, distributing
// them as evenly as possible across rows (e.g. 4 → [2,2] rather than [3,1]).
function distributeIntoRows(n: number, maxPerRow: number): number[] {
  if (n <= 0) return [];
  const rowCount = Math.ceil(n / maxPerRow);
  const base = Math.floor(n / rowCount);
  const remainder = n % rowCount;
  return Array.from({ length: rowCount }, (_, i) => base + (i < remainder ? 1 : 0));
}

const MultiCover = function MultiCoverComponent(props: CoverProps) {
  const p = props as Record<string, string>;
  const { clientName = "", periodLabel = "", sites = "" } = p;
  let siteList: MultiCoverSite[] = [];
  try {
    const arr = JSON.parse(sites);
    if (Array.isArray(arr)) {
      // Backward-compat: older reports stored a plain string[] of names.
      siteList = arr.map((entry) =>
        typeof entry === "string" ? { name: entry } : { name: entry?.name ?? "", photoUrl: entry?.photoUrl ?? "" }
      );
    }
  } catch {}
  const rowSizes = distributeIntoRows(siteList.length, MULTI_COVER_MAX_PER_ROW);
  // Tile width is always based on a 3-column basis, even when a row holds
  // fewer sites (e.g. the "2+2" layout for 4 sites) — keeps tile size
  // consistent across the whole cover instead of stretching to fill the row.
  const tileBasis = `calc((100% - ${MULTI_COVER_GAP * (MULTI_COVER_MAX_PER_ROW - 1)}px) / ${MULTI_COVER_MAX_PER_ROW})`;
  const rows: MultiCoverSite[][] = [];
  { let offset = 0; for (const size of rowSizes) { rows.push(siteList.slice(offset, offset + size)); offset += size; } }
  return (
    <div style={{ width: "100%", flex: 1, background: "#fafafa", display: "flex", flexDirection: "column", fontFamily: FF_BODY }}>
      <div data-a4-header="" style={{ background: "#000", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <img src="/logo.png" alt="logo" style={{ height: 26, objectFit: "contain", flexShrink: 0 }}/>
        <div style={{ fontFamily: FF_TITLE, fontSize: 8, letterSpacing: 3, color: "#00FF00", textTransform: "uppercase" }}>
          Rapport Mensuel Multi-Sites{periodLabel ? ` — ${periodLabel}` : ""}
        </div>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "20px 24px", minHeight: 0, overflow: "hidden" }}>
        {/* Text block — fixed at exactly 1/3 of the full A4 page height (not just the
            space left under the header bar), its 3 lines centered within it. */}
        <div style={{ height: "calc(297mm / 3)", flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          {/* data-cover-header: anchor for ReportA4's "Mettre à jour la couverture" overlay button. */}
          <div data-cover-header="sites" style={{ fontFamily: FF_TITLE, fontSize: 12, fontWeight: 900, color: "#666", textTransform: "uppercase", letterSpacing: 2, textAlign: "center", flexShrink: 0 }}>
            Contrat Management Énergétique
          </div>
          <div style={{ fontFamily: FF_TITLE, fontSize: 20, fontWeight: 900, color: "#1a1a2e", textTransform: "uppercase", letterSpacing: 1, marginTop: 12, textAlign: "center", flexShrink: 0 }}>
            {clientName}
          </div>
          <div style={{ fontFamily: FF_BODY, fontSize: 15, color: "#333", textAlign: "center", marginTop: 16, flexShrink: 0 }}>
            Bilan mensuel {periodLabel}
          </div>
        </div>
        {/* Photo grid — starts right at the 1/3 mark, top-aligned within the remaining 2/3. */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: MULTI_COVER_ROW_GAP }}>
          {rows.map((row, r) => (
            <div key={r} style={{ display: "flex", justifyContent: "center", gap: MULTI_COVER_GAP }}>
              {row.map((s, i) => (
                <div key={i} style={{ flex: `0 0 ${tileBasis}`, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  {/* Fixed aspect ratio — photo keeps its proportions instead of being
                      stretched to whatever height the row happens to have. justifyContent:
                      center on the tile (above) keeps the photo+title block centered when
                      a neighboring tile's wrapped title stretches this row's height. */}
                  <div style={{ width: "100%", aspectRatio: "4 / 3", background: "#222", borderRadius: 4, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {s.photoUrl ? (
                      <img src={s.photoUrl} alt={s.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}/>
                    ) : (
                      <span style={{ fontSize: 22, color: "#555" }}>📷</span>
                    )}
                  </div>
                  <div style={{ fontFamily: FF_TITLE, fontSize: 14, fontWeight: 900, color: "#1a1a2e", textAlign: "center", marginTop: 8, flexShrink: 0 }}>
                    {s.name}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE REGISTRY
// ═══════════════════════════════════════════════════════════════════════════════

export const TEMPLATES: ReportTemplate[] = [
  {
    id: "mensuel",
    label: "Rapport Mensuel",
    description: "Suivi mensuel",
    icon: "mensuel",
    CoverComponent: MonthlyCover,
    extraPages: [
      // Page 1: Bilan du mois — titre dynamique injecté à la création
      {
        title: "Bilan du mois",
        html: "<p>La consommation du site semble conforme à son utilisation.</p>",
        blocks: [],
      },
      // Page 2: Comparaison consommation électrique — daily + conso mensuelle
      {
        title: "Comparaison consommation électrique ⚡",
        html: "",
        blocks: [
          {
            type: "energy-elec-daily",
            title: "Consommation ELEC journalière",
            siteId: "", annee: new Date().getFullYear(), mois: new Date().getMonth()+1,
          } as any,
          {
            type: "energy-conso",
            title: "Comparaison ELEC mensuelle",
            energyType: "elec",
            siteId: "", annee: new Date().getFullYear(), mois: new Date().getMonth()+1,
            yearsBack: 2,
            moisOnly: true,   // show only the chosen month column across years
          } as any,
        ],
      },
      // Page 3: Comparaison consommation gaz
      {
        title: "Comparaison consommation gaz 🔥",
        html: "",
        blocks: [
          {
            type: "energy-conso",
            title: "Comparaison GAZ mensuelle",
            energyType: "gaz",
            siteId: "", annee: new Date().getFullYear(), mois: new Date().getMonth()+1,
            yearsBack: 2,
            moisOnly: true,
          } as any,
        ],
      },
    ],
  },
  {
    id: "trimestriel",
    label: "Rapport Trimestriel",
    description: "Bilan trimestriel",
    icon: "trimestriel",
    CoverComponent: QuarterlyCover,
    extraPages: [
      // Page 0: Détails installations CVC
      {
        title: "Détails installations CVC",
        html: "",
        blocks: [
          { type:"image", title:"Plans et détails CVC", url:"", alt:"Schéma des installations CVC" },
        ],
      },
      // Page 1: Synthèse
      {
        title: "Synthèse",
        html: "<p>La consommation du site semble conforme à son utilisation.</p>",
        blocks: [],
      },
      // Page 2: Consommation ELEC trimestrielle
      {
        title: "Consommation trimestrielle d'électricité ⚡",
        html: "",
        blocks: [
          {
            type: "energy-conso-quarter",
            title: "Consommation trimestrielle ELEC (kWh)",
            energyType: "elec",
            siteId: "", annee: new Date().getFullYear(), quarter: 1, yearsBack: 2,
          } as any,
        ],
      },
      // Page 3: Consommation journalière
      {
        title: "Consommation journalière",
        html: "",
        blocks: [
          {
            type: "energy-elec-daily",
            title: "Consommation ELEC journalière",
            siteId: "", annee: new Date().getFullYear(), mois: new Date().getMonth()+1,
          } as any,
        ],
      },
      // Page 4: Journée type par mois
      {
        title: "Journée type par mois",
        html: "",
        blocks: [
          {
            type: "energy-avg-day-per-month",
            title: "Journée type par mois",
            siteId: "", annee: new Date().getFullYear(), mois: new Date().getMonth()+1,
          } as any,
        ],
      },
      // Page 5: Semaine type
      {
        title: "Semaine type",
        html: "",
        blocks: [
          {
            type: "energy-avg-week-line",
            title: "Courbe Semaine Type",
            siteId: "", annee: new Date().getFullYear(), mois: new Date().getMonth()+1,
          } as any,
          {
            type: "energy-avg-week-heatmap",
            title: "Heatmap Semaine Type",
            siteId: "", annee: new Date().getFullYear(), mois: new Date().getMonth()+1,
          } as any,
        ],
      },
      // Page 6 (was 3): Consommation GAZ trimestrielle
      {
        title: "Consommation trimestrielle de gaz 🔥",
        html: "",
        blocks: [
          {
            type: "energy-conso-quarter",
            title: "Consommation trimestrielle GAZ",
            energyType: "gaz",
            siteId: "", annee: new Date().getFullYear(), quarter: 1, yearsBack: 2,
          } as any,
        ],
      },
      // Page 4: Plan d'actions
      {
        title: "Plan d'actions",
        html: "",
        blocks: [
          { type:"action", title:"Suivi des actions", rows:[] },
        ],
      },
    ],
  },
  {
    id: "annuel",
    label: "Rapport Annuel",
    description: "Bilan annuel complet",
    icon: "annuel",
    CoverComponent: AnnualCover,
    extraPages: [
      // Page 1: Bilan annuel — texte libre
      {
        title: "Bilan annuel",
        html: "<p>Synthèse des faits marquants</p>",
        blocks: [],
      },
      // Page 2: Comparaison consommation électrique
      {
        title: "Consommation annuelle d'électricité ⚡",
        html: "",
        blocks: [
          {
            type: "energy-conso-annuel",
            title: "Consommation annuelle ELEC",
            energyType: "elec",
            siteId: "", annee: new Date().getFullYear(), yearsBack: 2,
          } as any,
          {
            type: "energy-conso",
            title: "Consommation mensuelle ELEC",
            energyType: "elec",
            siteId: "", annee: new Date().getFullYear(), mois: 12, yearsBack: 2,
          } as any,
        ],
      },
      // Page 3: Profil de consommation semaine type
      {
        title: "Profil de consommation semaine type",
        html: "",
        blocks: [
          {
            type: "energy-avg-week-line",
            title: "Courbe Semaine Type",
            siteId: "", annee: new Date().getFullYear(), mois: 12,
          } as any,
        ],
      },
      // Page 4: Comparaison consommation gaz
      {
        title: "Consommation annuelle de gaz 🔥",
        html: "",
        blocks: [
          {
            type: "energy-conso-annuel",
            title: "Consommation annuelle GAZ",
            energyType: "gaz",
            siteId: "", annee: new Date().getFullYear(), yearsBack: 2,
          } as any,
          {
            type: "energy-conso",
            title: "Consommation mensuelle GAZ",
            energyType: "gaz",
            siteId: "", annee: new Date().getFullYear(), mois: 12, yearsBack: 2,
          } as any,
        ],
      },
      // Page 5: Signature énergétique chaufferie
      {
        title: "Signature énergétique chaufferie",
        html: "",
        blocks: [
          {
            type: "rigueur-climatique",
            title: "Influence climatique DJU R² (kWh)",
            energyType: "gaz",
            siteId: "", annee: new Date().getFullYear(),
          } as any,
        ],
      },
    ],
  },
  {
    id: "mensuel-multi",
    label: "Rapport Mensuel Multi-Sites",
    description: "Suivi mensuel de plusieurs sites d'un même client",
    icon: "mensuel",
    CoverComponent: MultiCover,
    // Placeholder only — le contenu réel (page de synthèse + une page par
    // site) est généré dynamiquement par buildMultiInstance() dans
    // ReportCreator.tsx, pas depuis ce tableau statique.
    extraPages: [
      { title: "Synthèse", html: "<p>Synthèse à venir.</p>", blocks: [] },
    ],
  },
];

export function getTemplate(id: string): ReportTemplate | undefined {
  // Normalise legacy English IDs saved before renaming
  const ALIAS: Record<string, string> = {
    monthly:   "mensuel",
    quarterly: "trimestriel",
    annual:    "annuel",
  };
  const normalised = ALIAS[id] ?? id;
  return TEMPLATES.find(t => t.id === normalised);
}
